import React from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../hooks/useEditMode';
import HeadlessTextArea from '../headless/textarea/HeadlessTextArea';
import { TextAlignment } from '../../enum/EnumRepository';
import { StyledDivComponent, StyledTextAreaComponent } from '../../dto/StyleCompRepository';
import { useStandardAlertStore } from '../../store/portal/alertStore';
import { INPUT_MATERIAL_MAX_ROWS } from '../../constants/limits';

interface MoleculeTextAreaDivProps {
  value: string | number;
  placeholder: string;
  defaultValue?: string | number;
  DefaultDiv?: StyledDivComponent;
  handleChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  textareaStyle: StyledTextAreaComponent;
  StyledDiv: StyledDivComponent;
  alignment?: TextAlignment;
  maxLength?: number;
  maxRows?: number;
}

const stripNewlines = (value: string): string => value.replace(/\r?\n/g, ' ');

const MoleculeTextAreaDiv: React.FC<MoleculeTextAreaDivProps> = ({
  value,
  placeholder,
  defaultValue,
  DefaultDiv,
  handleChange,
  textareaStyle,
  StyledDiv,
  alignment,
  maxLength,
  maxRows = INPUT_MATERIAL_MAX_ROWS,
}) => {
  const { isEditMode } = useEditMode();
  const { setTextLengthExceededAlert } = useStandardAlertStore();

  const valueStr = stripNewlines(value == null ? '' : String(value));
  const editAlignment = alignment || TextAlignment.LEFT;

  const handleChangeWithLimit = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextValue = stripNewlines(e.target.value);
    if (maxLength && nextValue.length > maxLength) {
      setTextLengthExceededAlert(maxLength);
      return;
    }
    e.target.value = nextValue;
    handleChange(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
      e.preventDefault();
    }
  };

  return (
    <>
      {isEditMode ? (
        <HeadlessTextAreaWrapper>
          {maxLength && (
            <LimitDiv>
              {valueStr.length} / {maxLength}
            </LimitDiv>
          )}
          <HeadlessTextArea
            alignment={editAlignment}
            content={valueStr}
            placeholder={placeholder}
            handleChange={handleChangeWithLimit}
            handleKeyDown={handleKeyDown}
            StyledTextArea={textareaStyle}
            rows={1}
            $minRows={1}
            $maxRows={maxRows}
          />
        </HeadlessTextAreaWrapper>
      ) : (
        !!valueStr ? (
          <StyledDiv $alignment={editAlignment}>{valueStr}</StyledDiv>
        ) : (
          DefaultDiv && defaultValue ? (
            <DefaultDiv>{defaultValue}</DefaultDiv>
          ) : null
        )
      )}
    </>
  );
};

const HeadlessTextAreaWrapper = styled.div`
  position: relative;
  width: 100%;
  flex: 1 1 auto;
  min-width: 0;
`;

const LimitDiv = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
  user-select: none;
  z-index: 1;
`;

export default MoleculeTextAreaDiv;
